/**
 * Deterministic randomness. Nothing in ToyRaiders may call `Math.random()` —
 * a room must look and behave identically for the owner, the raider and the
 * replay viewer, on every device, forever. Everything procedural (decor
 * scatter, wobble phases, crowd chatter) derives from the room seed.
 */

/** 32-bit integer hash. Same input → same output, no state. */
export function hash32(x: number, y = 0, z = 0): number {
  let h = (x | 0) * 374761393 + (y | 0) * 668265263 + (z | 0) * 2147483647
  h = (h ^ (h >>> 13)) * 1274126177
  h = h ^ (h >>> 16)
  return h >>> 0
}

/** Hash → float in [0,1). */
export function hashFloat(x: number, y = 0, z = 0): number {
  return hash32(x, y, z) / 4294967296
}

/** Small stateful PRNG (mulberry32) for one-shot sequences. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Stable seed from a string (room ids, player names). */
export function seedFromString(s: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}
